const QUIZ_FORM_TRACK_TIMEOUT = 3000;

class QuizForm extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.isSubmitting = false;
    this.hasTracked = false;
    this.trackTimeout = null;
    this.onOptionChange = this.onOptionChange.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onBackClick = this.onBackClick.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.progressSegments = Array.from(this.querySelectorAll('[data-quiz-progress-segment]'));
    this.backButton = this.querySelector('[data-quiz-back]');
    this.nextButton = this.querySelector('[data-quiz-next]');
    this.stepCounter = this.querySelector('[data-quiz-step-counter]');
    this.stepCounterSr = this.querySelector('[data-quiz-step-counter-sr]');
    this.stepsContainer = this.querySelector('[data-quiz-steps]');
    this.progressBar = this.querySelector('[role="progressbar"]');

    if (!this.form || this.steps.length === 0 || !this.nextButton) return;

    this.stepCounterTemplate = this.dataset.stepCounterTemplate || '__QUIZ_CURRENT__';
    this.nextLabel = this.nextButton.dataset.nextLabel || this.nextButton.textContent.trim();
    this.submitLabel = this.nextButton.dataset.submitLabel || this.nextLabel;

    this.stepsContainer.addEventListener('change', this.onOptionChange);
    this.nextButton.addEventListener('click', this.onNextClick);
    if (this.backButton) {
      this.backButton.addEventListener('click', this.onBackClick);
    }

    this.countrySelect = this.querySelector('[data-quiz-country]');
    if (this.countrySelect) {
      this.preselectCountry(this.countrySelect);
    }

    this.renderStep(false);
  }

  preselectCountry(select) {
    const defaultCountry = select.dataset.defaultCountry;
    if (!defaultCountry) return;

    const options = Array.from(select.options);
    const matchByText = options.find((option) => option.text.trim() === defaultCountry.trim());
    const matchByValue = options.find((option) => option.value === defaultCountry);
    const match = matchByText || matchByValue;

    if (match) {
      select.value = match.value;
    }
  }

  disconnectedCallback() {
    if (this.stepsContainer) {
      this.stepsContainer.removeEventListener('change', this.onOptionChange);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.onNextClick);
    }
    if (this.backButton) {
      this.backButton.removeEventListener('click', this.onBackClick);
    }
    if (this.trackTimeout) {
      clearTimeout(this.trackTimeout);
      this.trackTimeout = null;
    }
  }

  get lastIndex() {
    return this.steps.length - 1;
  }

  onOptionChange(event) {
    const input = event.target.closest('[data-quiz-option-input]');
    if (!input) return;

    const step = input.closest('[data-quiz-step]');
    if (!step) return;

    const stepIndex = step.dataset.quizStepIndex;
    const answerInput = step.querySelector(`[data-quiz-answer-question="${stepIndex}"]`);
    if (!answerInput) return;

    const checkedInputs = Array.from(step.querySelectorAll('[data-quiz-option-input]:checked'));
    answerInput.value = checkedInputs.map((checkedInput) => checkedInput.value).join(', ');
  }

  onBackClick() {
    if (this.currentIndex === 0) return;
    this.goToStep(this.currentIndex - 1);
  }

  onNextClick(event) {
    if (this.currentIndex === this.lastIndex) {
      if (!this.validateFinalStep()) {
        event.preventDefault();
        return;
      }
      if (this.hasTracked) return;

      event.preventDefault();
      this.startSubmission();
      return;
    }

    event.preventDefault();
    this.goToStep(this.currentIndex + 1);
  }

  validateFinalStep() {
    let isValid = true;
    const step = this.steps[this.currentIndex];
    const emailInput = step.querySelector('input[type="email"]');
    const gdprInput = step.querySelector('.agree-to-terms input[type="checkbox"]');
    const emailError = step.querySelector('[data-quiz-error-for="email"]');
    const gdprError = step.querySelector('[data-quiz-error-for="gdpr"]');

    if (emailInput && !emailInput.checkValidity()) {
      isValid = false;
      emailInput.setAttribute('aria-invalid', 'true');
      if (emailError) {
        emailError.hidden = false;
        emailInput.setAttribute('aria-describedby', emailError.id);
      }
      if (isValid === false && !this.firstInvalidField) {
        this.firstInvalidField = emailInput;
      }
    } else if (emailInput) {
      emailInput.removeAttribute('aria-invalid');
      if (emailError) emailError.hidden = true;
    }

    if (gdprInput && !gdprInput.checked) {
      isValid = false;
      gdprInput.setAttribute('aria-invalid', 'true');
      if (gdprError) {
        gdprError.hidden = false;
        gdprInput.setAttribute('aria-describedby', gdprError.id);
      }
      if (!this.firstInvalidField) {
        this.firstInvalidField = gdprInput;
      }
    } else if (gdprInput) {
      gdprInput.removeAttribute('aria-invalid');
      if (gdprError) gdprError.hidden = true;
    }

    if (!isValid && this.firstInvalidField) {
      this.firstInvalidField.focus();
    }
    this.firstInvalidField = null;

    return isValid;
  }

  startSubmission() {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.nextButton.disabled = true;

    const metricName = this.dataset.klaviyoMetric || 'Bespoke Quiz Completed';

    this.trackCompletion(metricName, this.collectAnswers()).then(() => {
      this.hasTracked = true;
      this.isSubmitting = false;
      this.nextButton.disabled = false;
      this.nextButton.click();
    });
  }

  identifyProfile(properties) {
    if (!window.klaviyo || typeof window.klaviyo.identify !== 'function') return Promise.resolve();
    if (!properties.email) return Promise.resolve();

    const profile = { $email: properties.email };

    if (properties.name) profile.$first_name = properties.name;
    if (properties.phone) profile.$phone_number = properties.phone;
    if (properties.country) profile.$country = properties.country;

    try {
      return Promise.resolve(window.klaviyo.identify(profile)).catch(() => {});
    } catch (error) {
      return Promise.resolve();
    }
  }

  trackCompletion(metricName, properties) {
    if (!window.klaviyo || typeof window.klaviyo.track !== 'function') {
      return Promise.resolve();
    }

    const dispatched = this.identifyProfile(properties)
      .then(() => window.klaviyo.track(metricName, properties))
      .catch(() => {});

    const timeout = new Promise((resolve) => {
      this.trackTimeout = setTimeout(resolve, QUIZ_FORM_TRACK_TIMEOUT);
    });

    return Promise.race([dispatched, timeout]);
  }

  collectAnswers() {
    const properties = {};
    let questionNumber = 0;

    this.steps.forEach((step) => {
      if (step.classList.contains('quiz-form__step--contact')) return;

      const legend = step.querySelector('[data-quiz-question-focus]');
      const answerInput = step.querySelector('[data-quiz-answer-input]');
      if (!legend || !answerInput) return;

      const questionText = legend.textContent.trim();
      questionNumber += 1;

      properties[questionText] = answerInput.value;
      properties[`question_${questionNumber}`] = questionText;
      properties[`answer_${questionNumber}`] = answerInput.value;
    });

    const contactStep = this.steps[this.lastIndex];
    if (contactStep) {
      const nameInput = contactStep.querySelector('input[type="text"]');
      const emailInput = contactStep.querySelector('input[type="email"]');
      const phoneInput = contactStep.querySelector('input[type="tel"]');
      const commentInput = contactStep.querySelector('textarea');
      const countrySelect = contactStep.querySelector('[data-quiz-country]');

      if (nameInput) properties.name = nameInput.value;
      if (emailInput) properties.email = emailInput.value;
      if (phoneInput) properties.phone = phoneInput.value;
      if (commentInput) properties.comment = commentInput.value;
      if (countrySelect && countrySelect.value) properties.country = countrySelect.value;
    }

    return properties;
  }

  goToStep(index) {
    if (index < 0 || index > this.lastIndex) return;

    this.steps[this.currentIndex].hidden = true;
    this.currentIndex = index;
    this.renderStep();
  }

  renderStep(shouldFocus = true) {
    const step = this.steps[this.currentIndex];
    step.hidden = false;

    if (shouldFocus) {
      const legend = step.querySelector('[data-quiz-question-focus]');
      if (legend) legend.focus();
    }

    if (this.backButton) {
      this.backButton.disabled = this.currentIndex === 0;
    }

    const isLastStep = this.currentIndex === this.lastIndex;
    this.nextButton.type = isLastStep ? 'submit' : 'button';
    const label = isLastStep ? this.submitLabel : this.nextLabel;
    const labelElement = this.nextButton.querySelector('span');
    if (labelElement) {
      labelElement.textContent = label;
    } else {
      this.nextButton.textContent = label;
    }
    if (this.nextButton.hasAttribute('aria-label')) {
      this.nextButton.setAttribute('aria-label', label);
    }

    this.progressSegments.forEach((segment, segmentIndex) => {
      segment.classList.toggle('quiz-form__progress-segment--active', segmentIndex === this.currentIndex);
      segment.classList.toggle('quiz-form__progress-segment--done', segmentIndex < this.currentIndex);
    });

    const counterText = this.stepCounterTemplate.replace('__QUIZ_CURRENT__', this.currentIndex + 1);
    if (this.stepCounter) this.stepCounter.textContent = counterText;
    if (this.stepCounterSr) this.stepCounterSr.textContent = counterText;

    if (this.progressBar) {
      this.progressBar.setAttribute('aria-valuenow', this.currentIndex + 1);
    }
  }
}

if (!customElements.get('quiz-form')) {
  customElements.define('quiz-form', QuizForm);
}
